import type {
  SloMetric,
  SloMetricStatus,
} from '@cast/features-cluster-shared-utils';
import { ClusterDisplayStatus } from '@cast/types';

/** Metrics shown as numeric values only (no Healthy/At risk/Unhealthy). */
const VALUE_ONLY_METRIC_IDS = new Set(['latency', 'reqRate']);

export type ReliabilityTooltipRow = {
  label: string;
  value: string;
  /** Token color key resolved by the React tooltip wrapper. */
  tone?: 'error' | 'warning' | 'success' | 'secondary';
};

const healthStatusLabel = (
  status: SloMetricStatus
): 'Healthy' | 'At risk' | 'Unhealthy' | null => {
  switch (status) {
    case 'breached':
      return 'Unhealthy';
    case 'at_risk':
      return 'At risk';
    case 'ok':
      return 'Healthy';
    default:
      return null;
  }
};

const healthStatusTone = (
  status: SloMetricStatus
): ReliabilityTooltipRow['tone'] => {
  switch (status) {
    case 'breached':
      return 'error';
    case 'at_risk':
      return 'warning';
    case 'ok':
      return 'success';
    default:
      return 'secondary';
  }
};

export const formatMetricDisplayValue = (metric: SloMetric): string => {
  if (!metric.unit) {
    return metric.value;
  }

  // Ratio-style units already include % /s etc. Keep value+unit adjacent.
  if (metric.unit.startsWith('/') || metric.unit === '%') {
    return `${metric.value}${metric.unit}`;
  }

  return `${metric.value} ${metric.unit}`;
};

export const buildReliabilityTooltipRows = (
  metrics: SloMetric[]
): ReliabilityTooltipRow[] =>
  metrics.map((metric) => {
    if (VALUE_ONLY_METRIC_IDS.has(metric.id)) {
      return {
        label: metric.label,
        value: formatMetricDisplayValue(metric),
      };
    }

    const statusLabel = healthStatusLabel(metric.status);
    if (!statusLabel) {
      return {
        label: metric.label,
        value: formatMetricDisplayValue(metric),
      };
    }

    return {
      label: metric.label,
      value: statusLabel,
      tone: healthStatusTone(metric.status),
    };
  });

/**
 * Match cluster dashboard install CTA: show when the cluster is discovered
 * (not connected / no agent), not based on which metrics happen to be in `slo[]`.
 */
export const shouldShowAgentInstallCta = (
  displayStatus?: ClusterDisplayStatus
): boolean => displayStatus === ClusterDisplayStatus.DISCOVERED;
